import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Card, Alert, Loader, Input } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import UsageCard from '@/components/apiUsage/UsageCard';
import WorkflowBinding from '@/components/apiUsage/WorkflowBinding';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

function parseScopes(scopes) {
  try {
    const v = JSON.parse(scopes || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

/** What the middleware will actually enforce, said in words (middleware/requireApiToken.js). */
function RateLimitBadge({ row }) {
  const v = row.rate_limit_per_minute;
  if (v === 0) return <Badge variant="warning" size="sm">no rate limiting</Badge>;
  if (v == null) return <Badge variant="secondary" size="sm">default · {row.rate_limit_default ?? 120}/min</Badge>;
  return <Badge variant="primary" size="sm">{v}/min</Badge>;
}

function TokenStatusBadge({ row }) {
  if (row.revoked_at) return <Badge variant="error" size="sm">revoked</Badge>;
  if (row.expires_at && new Date(row.expires_at) < new Date()) {
    return <Badge variant="warning" size="sm">expired</Badge>;
  }
  return <Badge variant="success" size="sm">active</Badge>;
}

export default function ApiTokenDetail() {
  const { id } = useParams();
  const { fetchApi } = useAppApi();

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [acting, setActing] = useState(false);
  const [revealed, setRevealed] = useState('');
  const [limit, setLimit] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);
  const [limitMsg, setLimitMsg] = useState('');

  useEffect(() => {
    fetchApi('crud', { model: 'ApiToken', action: 'show', where: { id } })
      .then((res) => {
        const r = res.data || res;
        setRow(r);
        // '' is a real choice here (fall back to the default), so null must not become 0.
        setLimit(r.rate_limit_per_minute == null ? '' : String(r.rate_limit_per_minute));
      })
      .catch((ex) => setErr(apiError(ex)))
      .finally(() => setLoading(false));
  }, [id]);

  const saveLimit = async () => {
    setSavingLimit(true);
    setErr('');
    setLimitMsg('');
    try {
      await fetchApi('crud', {
        model: 'ApiToken',
        action: 'setRateLimit',
        data: { id, rate_limit_per_minute: limit === '' ? null : Number(limit) },
      });
      const refreshed = await fetchApi('crud', { model: 'ApiToken', action: 'show', where: { id } });
      const r = refreshed.data || refreshed;
      setRow(r);
      setLimit(r.rate_limit_per_minute == null ? '' : String(r.rate_limit_per_minute));
      setLimitMsg('Saved — it applies to the next request this token makes.');
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSavingLimit(false);
    }
  };

  const rotate = async () => {
    if (!window.confirm('Rotating a token invalidates the current one and generates a new secret shown only once. Continue?')) return;
    setActing(true);
    setErr('');
    try {
      const res = await fetchApi('crud', { model: 'ApiToken', action: 'rotate', data: { id } });
      if (res.data && res.data.token) setRevealed(res.data.token);
      const refreshed = await fetchApi('crud', { model: 'ApiToken', action: 'show', where: { id } });
      setRow(refreshed.data || refreshed);
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setActing(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('A revoked token can never be used again. Continue?')) return;
    setActing(true);
    setErr('');
    try {
      await fetchApi('crud', { model: 'ApiToken', action: 'revoke', data: { id } });
      const refreshed = await fetchApi('crud', { model: 'ApiToken', action: 'show', where: { id } });
      setRow(refreshed.data || refreshed);
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setActing(false);
    }
  };

  if (loading) return <PageShell><Loader text="Loading token…" /></PageShell>;
  if (!row) return <PageShell><Alert variant="error" title="Could not load">{err || 'Token not found'}</Alert></PageShell>;

  return (
    <PageShell width="narrow" className="space-y-3">
      <PageHeader
        backTo="/api-token"
        backLabel="API Tokens"
        title={row.name || row.token_prefix}
        actions={(
          <Can module="core" action="manage_tokens">
            {!row.revoked_at && (
              <Button size="sm" variant="secondary" onClick={revoke} disabled={acting}>Revoke</Button>
            )}
            <Button size="sm" variant="primary" onClick={rotate} disabled={acting}>
              {acting ? 'Working…' : 'Rotate'}
            </Button>
          </Can>
        )}
      />

      <UsageCard tokenId={row.id} title="Usage (last 30 days)" />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      {revealed && (
        <Alert variant="warning" title="New token — copy it now, it will not be shown again">
          <code className="block break-all text-xs bg-[var(--app-surface)] rounded p-2 mt-2">{revealed}</code>
          <button className="text-sm text-primary-600 hover:underline mt-2" onClick={() => setRevealed('')}>Dismiss</button>
        </Alert>
      )}

      <Card className="p-3 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase text-secondary-500 mb-1">Token Prefix</div>
            <p className="text-sm font-mono">{row.token_prefix}…</p>
          </div>
          <div>
            <div className="text-xs uppercase text-secondary-500 mb-1">Status</div>
            <TokenStatusBadge row={row} />
          </div>
          <div>
            <div className="text-xs uppercase text-secondary-500 mb-1">Rate limit</div>
            <RateLimitBadge row={row} />
          </div>
          <div>
            <div className="text-xs uppercase text-secondary-500 mb-1">Company</div>
            <p className="text-sm">{row.company_name || '—'}</p>
          </div>
          <div>
            <div className="text-xs uppercase text-secondary-500 mb-1">Workflow</div>
            <WorkflowBinding workflowId={row.workflow_id} />
          </div>
          <div>
            <div className="text-xs uppercase text-secondary-500 mb-1">Created</div>
            <p className="text-sm text-secondary-500">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</p>
          </div>
          <div>
            <div className="text-xs uppercase text-secondary-500 mb-1">Expires</div>
            <p className="text-sm text-secondary-500">{row.expires_at ? new Date(row.expires_at).toLocaleString() : 'Never'}</p>
          </div>
          <div>
            <div className="text-xs uppercase text-secondary-500 mb-1">Revoked</div>
            <p className="text-sm text-secondary-500">{row.revoked_at ? new Date(row.revoked_at).toLocaleString() : '—'}</p>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase text-secondary-500 mb-2">Scopes</div>
          <div className="flex flex-wrap gap-1">
            {parseScopes(row.scopes).map((s) => (
              <span key={s} className="px-1.5 py-0.5 rounded bg-[var(--app-border)]/40 text-xs font-mono">{s}</span>
            ))}
          </div>
        </div>
      </Card>

      <Can module="core" action="manage_tokens">
        <Card className="p-3 space-y-3">
          <div>
            <h2 className="font-semibold">Rate limit</h2>
            <p className="text-sm text-secondary-500">
              How many requests a minute this token may make to the public API (<code>/api/v1</code>)
              and MCP server. Applies from the token&apos;s next request — nothing needs restarting.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56">
              <Input
                label="Requests per minute"
                type="number"
                min="0"
                step="1"
                placeholder={`Default (${row.rate_limit_default ?? 120}/min)`}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              />
            </div>
            <Button onClick={saveLimit} disabled={savingLimit}>
              {savingLimit ? 'Saving…' : 'Save'}
            </Button>
            {limit !== '' && (
              <Button variant="secondary" onClick={() => setLimit('')} disabled={savingLimit}>
                Use default
              </Button>
            )}
          </div>

          <ul className="text-xs text-secondary-500 space-y-1">
            <li><strong>0</strong> — no rate limiting at all for this token.</li>
            <li><strong>Empty</strong> — use the system default ({row.rate_limit_default ?? 120}/min).</li>
            <li>
              Any other number caps this token at that many requests a minute. The per-IP
              sub-limit is raised to match, so the number you set is the number you get.
            </li>
          </ul>

          {limitMsg && <Alert variant="success">{limitMsg}</Alert>}
        </Card>
      </Can>
    </PageShell>
  );
}
